import { type CSSProperties, useMemo } from 'react';
import { getVirtualTourType } from '@pkgs/components/utils/propertyCardUtils';
import { getUploadcareImageTransformationProps } from '@pkgs/components/utils/getUploadcareImageTransformationProps';
import { getObjectPosition } from '@pkgs/components/utils/getImageObjectPosition';
import { type BadgeItem } from '@pkgs/components/utils/badgesUtils';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { Badge, BadgeSize } from '@pkgs/components/components/Badge/Badge';
import { type UseCarouselActions } from '@pkgs/components';
import { getUploadCareImageUrl } from '@pkgs/api/index';

import { PageVariant } from '@/types/expose';
import { useExposeModal } from '@/providers/ExposeModalProvider';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { DEFAULT_IMAGE_ID } from '@/consts/image';
import { ImageComponent } from '@/components/common/ImageComponent/ImageComponent';

import { PropertyLockedPasswordCard } from './PropertyLockedPasswordCard/PropertyLockedPasswordCard';
import { UAExclusiveLockedInfoCard } from './PropertyLockedInfoCard/UAExclusiveLockedInfoCard';
import { PropertyLockedInfoCard } from './PropertyLockedInfoCard/PropertyLockedInfoCard';
import { PropertyImageSlider } from './PropertyImageSlider';
import { ActionButtons } from './ActionButtons';
import { TabKeys } from '../MediaTabs/MediaTabs.types';

import styles from './ExposeHeroImage.module.css';

export const ExposeHeroImagePrivateOffice = ({
  carouselActions,
  onImageClick,
  showsButtonGroup,
  badges,
}: {
  carouselActions: UseCarouselActions;
  onImageClick: () => void;
  showsButtonGroup: boolean;
  badges: BadgeItem[];
  isUAExclusiveListing?: boolean;
}) => {
  const { breakpoint = 0, isDesktop } = useBreakpoint();
  const { openMediaModal, setSelectedMediaTab, setSelectedMediaImageIndex } = useExposeModal();
  const {
    data: { listing, restrictedListing, genericListing },
  } = useListing();

  const { pageVariant } = useExposePageProps();
  const shouldHideUserAccountExclusiveContent = useShouldHideUserAccountExclusiveContent();

  const shouldShowActionButtons = showsButtonGroup && listing && !shouldHideUserAccountExclusiveContent;

  const vTourType = getVirtualTourType(listing?.vtourUrlNormalized, listing?.videoUrlNormalized);

  const lockedInfoComponent = useMemo(() => {
    if (shouldHideUserAccountExclusiveContent) {
      return <UAExclusiveLockedInfoCard />;
    } else if (!!restrictedListing && pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED) {
      return <PropertyLockedInfoCard restrictedListing={restrictedListing} />;
    }
    return null;
  }, [shouldHideUserAccountExclusiveContent, restrictedListing, pageVariant]);

  const images = useMemo(() => {
    const propertyImages = genericListing?.propertyImages;
    return propertyImages?.length ? propertyImages : [{ id: DEFAULT_IMAGE_ID, width: 360, height: 240 }];
  }, [genericListing.propertyImages]);
  const firstImage = images[0];

  return (
    <div className={styles.privateOfficeCarouselWrapper}>
      <div
        className={styles.container}
        onClick={() => {
          if (!restrictedListing && !shouldHideUserAccountExclusiveContent) {
            setSelectedMediaTab(TabKeys[isDesktop ? 'Photos' : 'Gallery']);
            setSelectedMediaImageIndex(carouselActions.selectedIndex);
            openMediaModal();
          }
        }}
      >
        <div className={styles.badgeContainer} data-private-office={true}>
          {badges.map(
            (badge) =>
              badge.label && (
                <Badge
                  size={BadgeSize.M}
                  key={badge.label}
                  label={badge.label}
                  variant={badge.variant}
                  data-testid={`expose-hero-image_badge_${badge.label}`}
                />
              ),
          )}
        </div>
        {(restrictedListing || shouldHideUserAccountExclusiveContent) && (
          <ImageComponent
            className={styles.restrictedOverlayImage}
            src={getUploadCareImageUrl({ imageId: firstImage?.id, useWaterMark: false, isBlurred: true })}
            style={{ '--object-position': getObjectPosition(firstImage) } as CSSProperties}
            alt="blurred property image for restricted listing"
            data-testid="expose-hero-image-restricted-listing-blurred-image"
            fill
            sizes="100vw"
            {...getUploadcareImageTransformationProps({ aspectRatio: '3/2', fit: 'automatic', image: firstImage })}
            uploadcareMediaAspectRatio="2/1"
            uploadcareMediaQuery="(min-width: 1240px)"
          />
        )}
        <PropertyImageSlider
          showsThumb={breakpoint <= Breakpoints.TABLET_LANDSCAPE}
          images={images}
          isPrivateOffice
          isRestrictedProperty={Boolean(restrictedListing) || shouldHideUserAccountExclusiveContent}
          carouselActions={carouselActions}
          hasWaterMark={Boolean(listing?.hasWaterMark)}
          onImageClick={() => {
            if (!restrictedListing && !shouldHideUserAccountExclusiveContent) {
              onImageClick();
            }
          }}
          isFirstImageBlurred={Boolean(restrictedListing) || shouldHideUserAccountExclusiveContent}
          shouldUnblurFirstImage={Boolean(listing) && !shouldHideUserAccountExclusiveContent}
        />
        {lockedInfoComponent}
        {Boolean(restrictedListing) && pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED_WITH_PASSWORD_PROTECTION && (
          <PropertyLockedPasswordCard />
        )}
        {shouldShowActionButtons && (
          <ActionButtons
            focusedImageIndex={carouselActions.selectedIndex + 1}
            totalImages={listing.propertyImages?.length ?? 0}
            hasFloorPlan={Boolean(listing.floorPlanImages)}
            vTourType={vTourType}
            onBtnClick={(tab) => {
              setSelectedMediaTab(tab);
              openMediaModal();
            }}
          />
        )}
      </div>
    </div>
  );
};
